import { useState, useEffect, useRef, Fragment } from "react";
import ChatQueueBlock from "../ChatQueueBlock/ChatQueueBlock";
import NewSessionDisclaimer from "../../../Shared/Components/NewSessionDisclaimer/NewSessionDisclaimer";
import { stopPlayingAudio, SESSION_ROW_BG } from "../../../Actions/Utils";

const ChatQueue = ({
  selectedLanguage,
  currentBlock,
  chatArr,
  micStatus,
  isLoading,
  stopAudioFlag,
  openSourceDialog = () => {},
  textSize,
  sendFeedback = () => {},
  submitResponse = () => {},
  audioRef,
  speakerEnabled,
  setSpeakerEnabled = () => {},
  markFavourite = () => {},
  setToastErrorMessage = () => {},
  setShowToastMessage = () => {},
  scrollToBottonFlag,
  start,
  setStartTour,
  onTourEnd,
  speakerOpen,
  setSpeakerOpen,
  defaultQuestionChipsClicked,
  setDefaultQuestionChipsClicked,
  questionChipClicked = () => {},
  currentSessionId = null,
  showNewChatPrompt = false,
  onNewChatClick = () => {},
}) => {
  const currQuesRef = useRef(null);
  const [requestController, setRequestController] = useState(null);
  // Initialize a variable to keep track of the previous date
  let prevDate = null;
  // Initialize a variable to keep track if the first date has been encountered
  let isFirstDateEncountered = false;
  let prevLang = null;

  const playAudio = async (url) => {
    const newAudio = new Audio(url);
    if (audioRef.current) {
      audioRef.current.pause();
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    if (requestController) {
      requestController.abort();
    }

    newAudio.addEventListener("ended", () => {
      setSpeakerEnabled(false);
    });

    audioRef.current = newAudio;
    newAudio.play().catch((err) => {
      console.error("Error playing audio:", err);
    });
  };

  const stopAudio = () => {
    if (audioRef.current) {
      setSpeakerEnabled(false);
      stopPlayingAudio(audioRef);
    }
  };

  const scrollToBottom = () => {
    if (currQuesRef.current) {
      setTimeout(() => {
        currQuesRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      }, 200); // Slight delay to ensure rendering completes
    }
  }

  useEffect(() => {
    stopAudio();
  }, [stopAudioFlag]);

  useEffect(scrollToBottom, [scrollToBottonFlag, currentBlock]);

  return (
    <div id="chatQueue" className="flex flex-col justify-between overflow-y-scroll mt-auto">
      {chatArr.length === 0 ? <></> : chatArr.map((itm, idx) => {
            // Check if the current date is different from the previous one
            const currentDate = new Date(itm?.bot?.datetime).toDateString(); // Assuming datetime is a JavaScript Date object
            const showDate =
              !prevDate ||
              (prevDate !== currentDate && currentDate !== "Invalid Date");
            prevDate = currentDate;
            
            // Language logic
            const currentLang = itm?.user?.language;
            const showLang = prevLang !== null && currentLang && currentLang !== prevLang;
            prevLang = currentLang;


            // Set isFirstDateEncountered to true once the first date is encountered
            if (!isFirstDateEncountered && showDate) {
              isFirstDateEncountered = true;
            }
            const isCurrentSessionRow =
              currentSessionId != null &&
              itm?.session_id != null &&
              itm.session_id === currentSessionId;
            const prevItem = idx > 0 ? chatArr[idx - 1] : null;
            const showNewSessionDisclaimer =
              itm?.session_id != null &&
              idx > 0 &&
              prevItem?.session_id !== itm.session_id;
            return (
              <Fragment key={idx}>
                {showNewSessionDisclaimer && (
                  <div className="flex justify-center px-2 pt-3 pb-1">
                    <NewSessionDisclaimer selectedLanguage={selectedLanguage} />
                  </div>
                )}
                <div
                  ref={idx === chatArr.length - 1 ? currQuesRef : null}
                  style={{
                    backgroundColor: isCurrentSessionRow ? SESSION_ROW_BG : undefined,
                  }}
                >
                  <ChatQueueBlock
                    idx={idx}
                    followingIdx={idx + 0.1}
                    item={itm}
                    isLastChatBlock={idx === chatArr.length - 1}
                    isLoading={isLoading}
                    selectedLanguage={selectedLanguage}
                    date={new Date(itm?.bot?.datetime)}
                    micStatus={micStatus}
                    requestController={requestController}
                    setRequestController={setRequestController}
                    openSourceDialog={openSourceDialog}
                    speakerEnabled={speakerEnabled}
                    setSpeakerEnabled={setSpeakerEnabled}
                    playAudio={playAudio}
                    stopAudio={stopAudio}
                    showDate={showDate}
                    showLang={showLang}
                    textSize={textSize}
                    sendFeedback={sendFeedback}
                    submitResponse={submitResponse}
                    disabled={idx < chatArr.length - 1}
                    markFavourite={markFavourite}
                    setToastErrorMessage={setToastErrorMessage}
                    setShowToastMessage={setShowToastMessage}
                    start={start}
                    setStartTour={setStartTour}
                    onTourEnd={onTourEnd}
                    speakerOpen={speakerOpen}
                    setSpeakerOpen={setSpeakerOpen}
                    defaultQuestionChipsClicked={defaultQuestionChipsClicked}
                    setDefaultQuestionChipsClicked={setDefaultQuestionChipsClicked}
                    questionChipClicked={questionChipClicked}
                    showNewChatPrompt={showNewChatPrompt}
                    onNewChatClick={onNewChatClick}
                  />
                </div>
              </Fragment>
            );
          })}
    </div>
  );
};

export default ChatQueue;
